<template>
  <div class="juou-card">
    <HpsfirProTable
      ref="tableRef"
      export-file-name="跳转小程序管理"
      title="跳转小程序管理"
      :paginations="{ pageSize: 10 }"
      :columns="columns"
      :get-data="getData"
      :on-delete="deleteFun"
      :on-edit="updateFun"
      :on-add="createFun"
      :loading="loading"
      :is-dangder-delete="true"
      :format-dangder-delete-info="formatDeleteInfo"
      row-key="id"
    />
  </div>
</template>
<script lang="tsx" setup>
import {
  useBaseAsync,
  type HpsfirProTableColumn,
  type HpsfirProTableData,
  type HpsfirProTableFilter,
  type HpsfirProTableGetDataFunction,
} from 'hpsfir-managebase-ui';

import { useApi } from '@/hooks/useApi';
import { ElMessage } from 'element-plus';
import { ref } from 'vue';
import dayjs from 'dayjs';
import { keyBy } from 'lodash';
import { normalizeBizTableColumns } from '@/utils/tableColumns';

// 小程序 appid -> 名称 映射
const weappMap = ref<Record<string, string>>({});

// 初始化获取小程序列表
const initWeappMap = async() => {
  try {
    const res = await useApi().weappApi.pageWeappweapp({
      conditions: {},
      pageQuery: { page: 0, pageSize: 1000 },
    });
    const weappList = res.data.data?.data || [];
    weappMap.value = keyBy(weappList.map((item: any) => ({
      appid: item.appid,
      name: item.name || item.appid,
    })), 'appid');
  } catch (err) {
    console.error('获取小程序列表失败', err);
  }
};

// 根据 appid 获取小程序名称
const getWeappName = (appid: string) => (weappMap.value[appid] as any)?.name || appid;

initWeappMap();

const columns = ref<HpsfirProTableColumn[]>(normalizeBizTableColumns([
  {
    name: 'name',
    label: '目标小程序名称',
    type: 'string',
    isCondition: true,
    rule: [{ required: true }],
  },
  {
    name: 'targetAppid',
    label: '目标小程序AppID',
    type: 'string',
    isCondition: true,
    rule: [{ required: true }],
  },
  {
    name: 'targetPath',
    label: '目标路径',
    type: 'string',
    isCondition: true,
  },
  {
    name: 'nfcUrl',
    label: 'NFC URL',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    render: (scope: any) => {
      const row = scope?.row || {};
      const { nfcUrl } = row;
      if (!nfcUrl || typeof nfcUrl !== 'object') {
        return '-';
      }
      return (
        <div class="url-list">
          {Object.entries(nfcUrl).map(([appid, url]) => (
            <div class="url-item" key={appid}>
              <span class="url-label">{getWeappName(appid)}:</span>
              <span class="url-value">{url as string}</span>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    name: 'qrcodeUrl',
    label: '二维码 URL',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    render: (scope: any) => {
      const row = scope?.row || {};
      const { qrcodeUrl } = row;
      if (!qrcodeUrl || typeof qrcodeUrl !== 'object') {
        return '-';
      }
      return (
        <div class="qrcode-list">
          {Object.entries(qrcodeUrl).map(([appid, url]) => (
            <div class="qrcode-item" key={appid}>
              <span class="qrcode-label">{getWeappName(appid)}:</span>
              <img class="qrcode-img" src={useApi().baseUrl + url} alt={appid} />
            </div>
          ))}
        </div>
      );
    },
  },
  {
    name: 'createTime',
    label: '创建时间',
    type: 'string',
    width: 180,
    hideCreate: true,
    hideEdit: true,
    render: (scope: any) => (scope?.row?.createTime ? dayjs(scope.row.createTime).format('YYYY-MM-DD HH:mm:ss') : '-'),
  },
]));

const tableRef = ref<any>();
const loading = ref(false);

const getData = useBaseAsync<HpsfirProTableGetDataFunction>(async(filter: HpsfirProTableFilter) => {
  try {
    loading.value = true;
    const res = await useApi().jumpWeappApi.pageJumpWeappjumpWeapp({
      conditions: Object.entries(filter.conditions).reduce((conditions, [key, val]) => {
        if (val === null || val === undefined || val === '') {
          return conditions;
        }
        conditions[key] = val;
        return conditions;
      }, {} as any),
      pageQuery: {
        page: filter.paginations.page - 1,
        pageSize: filter.paginations.pageSize,
      },
    });
    const tableData: HpsfirProTableData = {
      total: res.data.data?.total || 0,
      data: res.data.data?.data || [],
    };
    return tableData;
  } finally {
    loading.value = false;
  }
});

const formatDeleteInfo = (row: any) => `您正在进行删除(${row.name || row.targetAppid})操作，请谨慎操作`;

const createFun = useBaseAsync(async(data: any) => {
  await useApi().jumpWeappApi.addJumpWeappjumpWeapp({
    jumpWeappAdminVo: data,
  });
  ElMessage.success('添加成功');
});

const updateFun = useBaseAsync(async(data: any) => {
  await useApi().jumpWeappApi.updateJumpWeappjumpWeapp({
    jumpWeappAdminVo: data,
  });
  ElMessage.success('修改成功');
});

const deleteFun = useBaseAsync(async(data: any) => {
  await useApi().jumpWeappApi.delJumpWeappjumpWeapp(data.id);
  ElMessage.success('删除成功');
});
</script>
<style lang="scss" scoped>
:deep(.url-list),
:deep(.qrcode-list) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

:deep(.url-item),
:deep(.qrcode-item) {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #409eff;
}

:deep(.url-label),
:deep(.qrcode-label) {
  color: #606266;
  font-size: 12px;
  font-weight: 500;
}

:deep(.url-value) {
  color: #409eff;
  font-size: 13px;
  word-break: break-all;
  padding: 4px 8px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

:deep(.qrcode-img) {
  width: 80px;
  height: 80px;
  object-fit: contain;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 4px;
  margin-top: 4px;
}
</style>
